import type { AliasResolveResult } from "@multisystemsuite/timezone-engine-shared-types";
import { isValidIanaTimezone, normalizeTimezone } from "./normalization.js";

/** Abbreviations that map to multiple regions — never auto-resolve */
export const AMBIGUOUS_TIMEZONE_ABBREVIATIONS = new Set([
  "IST",
  "CST",
  "EST",
  "PST",
  "MST",
  "AST",
  "BST",
  "GST",
  "CAT",
  "WAT",
  "SST",
  "NST",
]);

/** Safe legacy and regional alias mappings to canonical IANA ids */
const SAFE_IANA_ALIASES: Record<string, string> = {
  GMT: "UTC",
  Greenwich: "UTC",
  Zulu: "UTC",
  "US/Eastern": "America/New_York",
  "US/Central": "America/Chicago",
  "US/Mountain": "America/Denver",
  "US/Pacific": "America/Los_Angeles",
  "US/Alaska": "America/Anchorage",
  "US/Hawaii": "Pacific/Honolulu",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Rangoon": "Asia/Yangon",
  "Europe/Kiev": "Europe/Kyiv",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
};

const INVALID_PATTERNS = [/^Asia\/India$/i, /^US\/NewYork$/i, /^US\/LosAngeles$/i, /^Europe\/UK$/i];

function lookupAlias(value: string): string | undefined {
  const direct = SAFE_IANA_ALIASES[value];
  if (direct) return direct;

  const lower = value.toLowerCase();
  for (const [alias, iana] of Object.entries(SAFE_IANA_ALIASES)) {
    if (alias.toLowerCase() === lower) return iana;
  }
  return undefined;
}

/**
 * Resolve known timezone aliases to canonical IANA ids.
 * Rejects ambiguous abbreviations (IST, CST, etc.) and invalid legacy names.
 */
export function aliasTimezoneResolver(input: string | null | undefined): AliasResolveResult {
  const normalized = normalizeTimezone(input);

  if (!input || normalized === "UTC") {
    return { input: normalized, resolved: "UTC", reason: "valid_iana" };
  }

  if (INVALID_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return { input: normalized, resolved: null, reason: "invalid" };
  }

  if (AMBIGUOUS_TIMEZONE_ABBREVIATIONS.has(normalized.toUpperCase())) {
    return { input: normalized, resolved: null, reason: "ambiguous" };
  }

  const alias = lookupAlias(normalized);
  if (alias && isValidIanaTimezone(alias)) {
    return { input: normalized, resolved: alias, reason: "alias" };
  }

  if (isValidIanaTimezone(normalized)) {
    return { input: normalized, resolved: normalized, reason: "valid_iana" };
  }

  return { input: normalized, resolved: null, reason: "invalid" };
}
